import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ChevronLeft, ChevronRight, ClipboardList, Printer } from 'lucide-react';
import {
  addDays,
  allocateShelf,
  formatMoney,
  shopDate,
  type BakeSheetDay,
  type BakeSheetProduct,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import { SheetColumnsModal } from './SheetColumns.js';
import { bakeSheetChanged } from '../lib/invalidate.js';
import { buildKitchenSheetHtml } from '../lib/kitchen-print.js';
import { openPrintWindow } from '../lib/printing.js';
import { OrderDetail } from './Orders.js';
import { CustomerDetail } from './Customers.js';
import { Button, cx, EmptyState, Input, ROOMY_TARGET, Skeleton, useToast } from '../components/ui.js';

/**
 * The shop's day on one page.
 *
 * It replaces a spreadsheet, and deliberately looks like one: a row per order,
 * a column per good, the totals underneath. Thirty-five columns do not fit on
 * any screen, so the grid scrolls sideways with the name column pinned — the
 * same thing the shop already does by freezing panes.
 *
 * Only two rows take typing. Everything else is either counted from the orders
 * or worked out from the rows above it, which is what stops the sheet drifting
 * away from what was actually sold.
 */
export function BakeSheetScreen() {
  const [today, setToday] = useState(() => shopDate());
  const [date, setDate] = useState(today);
  const [printing, setPrinting] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const toast = useToast();
  const day = useQuery({
    queryKey: ['bake-sheet', date],
    queryFn: () => api.bakeSheet.day(date),
    /*
     * An order rung up on this device refreshes the sheet the moment it is
     * saved. One rung up on another till cannot, so the sheet also asks. Half a
     * minute is short enough that the kitchen list is never meaningfully behind
     * the counter, and long enough to be invisible on shop wifi.
     */
    refetchInterval: 30_000,
  });

  /*
   * The sheet opens on today and stays on today.
   *
   * A shop screen is not reloaded from one week to the next, so a view left up
   * overnight would still be showing yesterday when the first order of the
   * morning lands on it. Only a view already sitting on today follows the clock
   * over — somebody who has deliberately gone back a day is left where they are.
   */
  useEffect(() => {
    const tick = () => {
      const now = shopDate();
      if (now === today) return;
      setDate((current) => (current === today ? now : current));
      setToday(now);
    };
    const timer = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [today]);

  const dayName = (iso: string) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });

  const weekday = useMemo(
    () => new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' }),
    [date],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-lg font-semibold">Bake sheet</h1>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {weekday}
            {/*
              * Where the shelf came from, named rather than assumed. Monday
              * inherits Friday, and seeing that written down is the only way
              * to tell a weekend being skipped from a figure going missing.
              */}
            {day.data?.carriedFrom && <> · shelf carried from {dayName(day.data.carriedFrom)}</>}
            {day.data && !day.data.isOpenDay && <> · shop closed this day</>}
          </p>
        </div>

        {/*
          * Wraps on a narrow screen. Adding the Columns button pushed this row
          * past the edge of a phone and carried Kitchen off with it — the one
          * control somebody standing at the oven actually reaches for.
          */}
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Previous day"
            onClick={() => setDate((current) => addDays(current, -1))}
            icon={<ChevronLeft className="size-4" />}
          />
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-40" />
          <Button
            size="sm"
            variant="ghost"
            aria-label="Next day"
            onClick={() => setDate((current) => addDays(current, 1))}
            icon={<ChevronRight className="size-4" />}
          />
          <Button size="sm" variant={date === today ? 'primary' : undefined} onClick={() => setDate(today)}>
            Today
          </Button>
          {/* Reached from the sheet, because that is where you notice a column
              is in the wrong place. */}
          <Button
            size="sm"
            variant="ghost"
            icon={<ArrowLeftRight className="size-4" />}
            disabled={!day.data || day.data.products.length === 0}
            onClick={() => setColumnsOpen(true)}
          >
            Columns
          </Button>
          {/*
            * The same day, panned up for the oven. Fetched on the click rather
            * than kept warm beside the sheet, because it is wanted once a
            * morning and must be the figures as they stand at that moment.
            */}
          <Button
            size="sm"
            variant="primary"
            icon={<Printer className="size-4" />}
            loading={printing}
            onClick={async () => {
              setPrinting(true);
              try {
                const sheet = await api.bakeSheet.kitchen(date);
                openPrintWindow(buildKitchenSheetHtml(sheet, weekday), `Kitchen sheet ${date}`);
              } catch (error) {
                toast.push({
                  tone: 'danger',
                  title: 'Could not build the kitchen sheet',
                  body: (error as Error).message,
                });
              } finally {
                setPrinting(false);
              }
            }}
          >
            Kitchen
          </Button>
        </div>
      </header>

      {day.isLoading && <Skeleton className="m-4 h-64" />}
      {day.data && <Sheet day={day.data} />}
      {columnsOpen && day.data && (
        <SheetColumnsModal products={day.data.products} onClose={() => setColumnsOpen(false)} />
      )}
    </div>
  );
}

/**
 * When an order baked early is actually handed over.
 *
 * Only ever a day or a few ahead, so the weekday and the time are enough —
 * "Mon 9:00 am" reads faster on a crowded row than a full date, and the baker
 * only needs to know it is not today.
 */
function collectedAt(dueAt: string): string {
  return new Date(dueAt).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** A band of flavours mixed from one batch, drawn over the columns it covers. */
interface Span {
  key: string | null;
  label: string;
  total: number;
  width: number;
}

function bandSpans(products: BakeSheetProduct[], bands: BakeSheetDay['doughBands']): Span[] {
  const spans: Span[] = [];
  for (const product of products) {
    const previous = spans[spans.length - 1];
    if (previous && previous.key === product.doughGroup) {
      previous.width += 1;
      continue;
    }
    const band = bands.find((candidate) => candidate.key === product.doughGroup);
    spans.push({
      key: product.doughGroup,
      label: band?.label ?? '',
      total: band?.kitchenTotal ?? 0,
      width: 1,
    });
  }
  return spans;
}

/**
 * Colour a band the way the spreadsheet does, so the eye finds it.
 *
 * Every band needs an entry here, and anything unrecognised still has to come
 * back opaque: this row is part of a sticky header, so a cell with no
 * background of its own is a window onto the order rows sliding underneath it.
 * A loaf count from halfway down the sheet appearing inside the band heading
 * reads as a wrong total rather than as a drawing mistake.
 */
const BAND_TINT: Record<string, string> = {
  plain: 'bg-[var(--color-info-soft)]',
  rosemary: 'bg-[var(--color-brand-soft,var(--color-surface-sunken))]',
  cheese: 'bg-[var(--color-warn-soft,var(--color-surface-sunken))]',
  /*
   * Ordered so no two neighbours are the same temperature: blue, cream,
   * yellow, green, tan reading across. Loaves takes the green specifically
   * because it sits against the cheesy yellow, and two warm tints side by side
   * stop being a boundary at a glance.
   *
   * All five are the semantic `-soft` tokens rather than raw palette steps,
   * because those are the ones redefined for dark mode — a fixed light tan
   * would turn into pale text on a pale band at night.
   */
  bread: 'bg-[var(--color-ok-soft)]',
  big: 'bg-[var(--color-accent-soft)]',
};

/** The band's colour, never transparent. */
function bandTint(key: string | null): string {
  return (key && BAND_TINT[key]) || 'bg-[var(--color-surface)]';
}

function Sheet({ day }: { day: BakeSheetDay }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * Opened over the day, not instead of it.
   *
   * Going to the order screen and back meant losing the date, the sideways
   * scroll and the place in a page of order rows — a lot to rebuild just to
   * check what somebody ordered. These are the same cards those screens show;
   * closing one puts the sheet back exactly as it was, because it never left.
   */
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const [openCustomer, setOpenCustomer] = useState<string | null>(null);

  // Shared with the orders screen, so it is usually already in hand.
  const statuses = useQuery({ queryKey: ['order-statuses'], queryFn: () => api.orders.statuses() });
  const products = day.products;
  const spans = useMemo(() => bandSpans(products, day.doughBands), [products, day.doughBands]);

  const save = useMutation({
    mutationFn: ({ productId, values }: { productId: string; values: { carriedIn?: number; restock?: number; baked?: number } }) =>
      api.bakeSheet.set(day.date, productId, values),
    // Through the helper, so the chat's Available-now list is refreshed with
    // the sheet rather than quoting a figure that has just changed.
    onSuccess: () => bakeSheetChanged(queryClient),
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  if (products.length === 0) {
    return (
      <EmptyState
        icon={<ClipboardList className="size-10" />}
        title="Nothing on the sheet"
        description="No active products are set to show here."
      />
    );
  }

  const live = day.orders.filter((order) => !order.isCancelled);

  /*
   * Which order is being handed yesterday's bread.
   *
   * The shelf is finite, so it goes to the orders at the top of the day and
   * runs out partway down. Walking the rows in the order they are shown makes
   * that visible and keeps it stable: the same cells are marked every time the
   * sheet is opened, rather than wandering between customers.
   *
   * It matters because those loaves are a day older than the rest of the order,
   * and whoever is packing should know before the customer does.
   */
  const fromShelf = useMemo(() => {
    const taken = new Map<string, number>();
    for (const product of day.products) {
      const allocation = allocateShelf({
        carriedIn: product.carriedIn,
        claim: product.shelfClaim,
        orders: live.map((order) => ({
          orderId: order.orderId,
          qty: order.quantities[product.productId] ?? 0,
        })),
      });
      for (const [orderId, units] of allocation) taken.set(`${orderId}|${product.productId}`, units);
    }
    return taken;
  }, [day.products, live]);

  /**
   * Hand this product's shelf stock to this order instead.
   *
   * Written as "serve this one first" rather than a fixed assignment, so the
   * rest of the queue carries on behind it and an order that later leaves the
   * day simply stops counting. Tapping an order that already holds the shelf
   * takes it back off, which puts the sheet back to deciding for itself.
   */
  const claimShelf = useMutation({
    mutationFn: ({ productId, orderId, held }: { productId: string; orderId: string; held: boolean }) => {
      const product = day.products.find((candidate) => candidate.productId === productId);
      const current = product?.shelfClaim ?? [];
      const next = held
        ? current.filter((id) => id !== orderId)
        : [orderId, ...current.filter((id) => id !== orderId)];
      return api.bakeSheet.set(day.date, productId, { shelfClaim: next });
    },
    onSuccess: () => bakeSheetChanged(queryClient),
    onError: (error: Error) =>
      toast.push({ tone: 'danger', title: 'Could not move it', body: error.message }),
  });
  const kitchenGrand = products.reduce((sum, product) => sum + product.kitchenTotal, 0);
  /* The spreadsheet's `#` column, added up — what the day has been promised. */
  const orderedUnits = products.reduce((sum, product) => sum + product.ordered, 0);

  /** Every cell in the grid is the same box; only what fills it differs. */
  const cell = 'border-l border-[var(--color-line)] px-1 py-1 text-center tabular text-sm';
  const sticky = 'sticky left-0 z-10 bg-[var(--color-surface)] px-3 py-1 text-left';

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-20">
          {/* Dough bands, as the spreadsheet draws them ------------------- */}
          <tr>
            <th className={cx(sticky, 'z-30 border-b border-[var(--color-line)]')} />
            {spans.map((span, index) => (
              <th
                key={index}
                colSpan={span.width}
                className={cx(
                  'border-b border-l border-[var(--color-line)] px-2 py-1 text-xs font-medium',
                  bandTint(span.key),
                )}
              >
                {span.key && (
                  <span className="whitespace-nowrap">
                    {span.label} · <strong className="tabular">{span.total}</strong>
                  </span>
                )}
              </th>
            ))}
          </tr>

          <tr className="bg-[var(--color-surface)]">
            <th className={cx(sticky, 'z-30 border-b border-[var(--color-line)] text-left text-xs font-semibold')}>
              <span className="block w-56 truncate">
                {live.length} order{live.length === 1 ? '' : 's'} · {orderedUnits} item
                {orderedUnits === 1 ? '' : 's'}
              </span>
            </th>
            {products.map((product) => (
              <th
                key={product.productId}
                title={product.name}
                className={cx(cell, 'border-b bg-[var(--color-surface)] text-xs font-semibold')}
              >
                <span className="block w-14 truncate">{product.label}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {live.length === 0 && (
            <tr>
              <td className={cx(sticky, 'text-xs text-[var(--color-ink-faint)]')}>No orders for this day yet</td>
              {products.map((product) => (
                <td key={product.productId} className={cell} />
              ))}
            </tr>
          )}

          {live.map((order) => (
            /*
             * Blue means this one leaves before the ovens next run, so it is
             * baked today and kept overnight. The baker needs to see it on
             * today's list even though the customer is not coming until
             * tomorrow morning — it is the row most easily made a day late.
             */
            <tr
              key={order.orderId}
              className={cx(
                order.bakedAhead ? 'bg-[var(--color-info-soft)]' : 'hover:bg-[var(--color-surface-sunken)]',
              )}
            >
              <td className={cx(sticky, 'border-t border-[var(--color-line)]')}>
                {/*
                  * Both of these are ways somewhere, so both are links. Reading
                  * a name off the sheet and then hunting for the same person
                  * under Clients is the detour this removes — the same one the
                  * inbox already removes on its own header.
                  *
                  * A walk-in has no client card, so the name stays plain text
                  * rather than becoming a link that goes nowhere.
                  */}
                <span className="block w-56 truncate text-sm">
                  {order.customerId ? (
                    <button
                      type="button"
                      onClick={() => setOpenCustomer(order.customerId)}
                      className={cx(
                        ROOMY_TARGET,
                        'rounded-[var(--radius-sm)] font-medium text-[var(--color-info)] underline decoration-dotted underline-offset-2 transition hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-info)]',
                      )}
                    >
                      {order.customerName}
                    </button>
                  ) : (
                    order.customerName
                  )}
                </span>
                <span className="block w-56 truncate text-xs text-[var(--color-ink-faint)]">
                  <button
                    type="button"
                    onClick={() => setOpenOrder(order.orderId)}
                    className={cx(
                      ROOMY_TARGET,
                      'rounded-[var(--radius-sm)] text-[var(--color-info)] underline decoration-dotted underline-offset-2 transition hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-info)]',
                    )}
                  >
                    {order.orderNo}
                  </button>
                  {' · '}
                  {order.itemCount} · {formatMoney(order.total)}
                  {order.balanceDue === 0 ? ' · paid' : ''}
                  {/* Which morning it is collected, since it is not this one. */}
                  {order.bakedAhead && order.dueAt && (
                    <span className="font-medium text-[var(--color-info)]"> · out {collectedAt(order.dueAt)}</span>
                  )}
                </span>
              </td>
              {products.map((product) => {
                const qty = order.quantities[product.productId] ?? 0;
                const shelfUnits = fromShelf.get(`${order.orderId}|${product.productId}`) ?? 0;
                const partly = shelfUnits > 0 && shelfUnits < qty;
                return (
                  <td
                    key={product.productId}
                    className={cx(
                      cell,
                      'border-t p-0',
                      /*
                        * One colour for "there is yesterday's bread in this
                        * one", whether it covers the line or only part of it.
                        * A split was its own amber for a while, which read as
                        * a milder thing than it is — the packer has the same
                        * job either way, and the "n shelf" line underneath is
                        * what says how much.
                        */
                      shelfUnits > 0 && 'bg-[var(--color-danger-soft)]',
                    )}
                  >
                    {qty ? (
                      /*
                        * The cell is a button so the shelf can be moved to
                        * whoever is collecting first. Tapping one that already
                        * holds it hands it back, and the sheet goes back to
                        * working it out on its own.
                        */
                      <button
                        type="button"
                        disabled={product.carriedIn <= 0 || claimShelf.isPending}
                        onClick={() =>
                          claimShelf.mutate({
                            productId: product.productId,
                            orderId: order.orderId,
                            held: shelfUnits > 0,
                          })
                        }
                        title={
                          product.carriedIn <= 0
                            ? undefined
                            : shelfUnits === 0
                              ? `Give ${order.customerName ?? 'this order'} the ${product.label} from the shelf`
                              : partly
                                ? `${shelfUnits} of ${qty} from the shelf, ${qty - shelfUnits} to bake — tap to hand it back`
                                : 'From the shelf — baked on an earlier day. Tap to hand it back'
                        }
                        className="w-full cursor-pointer px-1 py-1 leading-tight disabled:cursor-default"
                      >
                        {qty}
                        {/* Only when it is split: a whole cell says it with colour alone. */}
                        {partly && (
                          <span className="block text-[9px] font-medium text-[var(--color-ink-soft)]">
                            {shelfUnits} shelf
                          </span>
                        )}
                      </button>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        <tfoot className="sticky bottom-0 z-20">
          <SummaryRow
            label="Carried in"
            hint="On the shelf at opening"
            products={products}
            cellClass={cell}
            stickyClass={sticky}
            tint="bg-[var(--color-surface-sunken)]"
            render={(product) => (
              <NumberCell
                value={product.carriedIn}
                muted={!product.carriedCounted}
                onSave={(carriedIn) => save.mutate({ productId: product.productId, values: { carriedIn } })}
              />
            )}
          />

          <SummaryRow
            label="Ordered"
            hint={`Counted from the orders above · ${orderedUnits} in all`}
            products={products}
            cellClass={cell}
            stickyClass={sticky}
            tint="bg-[var(--color-surface-sunken)]"
            render={(product) => (product.ordered ? <span>{product.ordered}</span> : null)}
          />

          <SummaryRow
            label="Bake for restock"
            hint="Extra for the shelf — your call"
            products={products}
            cellClass={cell}
            stickyClass={sticky}
            tint="bg-[var(--color-ok-soft,var(--color-surface-sunken))]"
            render={(product) => (
              <NumberCell
                value={product.restock}
                onSave={(restock) => save.mutate({ productId: product.productId, values: { restock } })}
              />
            )}
          />

          <SummaryRow
            label="TOTAL FOR KITCHEN"
            hint={`To bake once the shelf is used · ${kitchenGrand} in all`}
            products={products}
            cellClass={cell}
            stickyClass={sticky}
            tint="bg-[var(--color-surface)]"
            strong
            render={(product) => (product.kitchenTotal ? <strong>{product.kitchenTotal}</strong> : null)}
          />

          <SummaryRow
            label="Available"
            hint="Spare once the orders are covered"
            products={products}
            cellClass={cell}
            stickyClass={sticky}
            tint="bg-[var(--color-surface-sunken)]"
            render={(product) => {
              if (!product.available) return null;
              /*
               * Below zero means more has been promised than is being made —
               * usually orders that landed after the list went to the kitchen.
               * It is the one square here somebody has to do something about.
               */
              return (
                <span className={cx(product.available < 0 && 'font-semibold text-[var(--color-danger)]')}>
                  {product.available}
                </span>
              );
            }}
          />
        </tfoot>
      </table>

      {openOrder && (
        <OrderDetail orderId={openOrder} statuses={statuses.data ?? []} onClose={() => setOpenOrder(null)} />
      )}
      {openCustomer && <CustomerDetail customerId={openCustomer} onClose={() => setOpenCustomer(null)} />}
    </div>
  );
}

function SummaryRow({
  label,
  hint,
  products,
  render,
  cellClass,
  stickyClass,
  tint,
  strong,
}: {
  label: string;
  hint: string;
  products: BakeSheetProduct[];
  render: (product: BakeSheetProduct) => React.ReactNode;
  cellClass: string;
  stickyClass: string;
  tint: string;
  strong?: boolean;
}) {
  return (
    <tr className={tint}>
      <td className={cx(stickyClass, tint, 'border-t border-[var(--color-line)]')}>
        <span className={cx('block w-56 truncate text-sm', strong && 'font-semibold')}>{label}</span>
        <span className="block w-56 truncate text-xs text-[var(--color-ink-faint)]">{hint}</span>
      </td>
      {products.map((product) => (
        <td key={product.productId} className={cx(cellClass, tint, 'border-t')}>
          {render(product)}
        </td>
      ))}
    </tr>
  );
}

/**
 * A number you can type over.
 *
 * Saves when the box is left rather than on every keystroke: somebody counting
 * a shelf types "1", then "12", and a request per character would write the
 * first of those as a fact. Held locally while focused so the cursor is not
 * yanked by a refetch landing mid-count.
 */
function NumberCell({ value, onSave, muted }: { value: number; onSave: (next: number) => void; muted?: boolean }) {
  const [draft, setDraft] = useState(value === 0 ? '' : String(value));
  const [editing, setEditing] = useState(false);

  // An empty box rather than a zero, so a figure can be typed straight in
  // instead of being typed around a character nobody meant to enter.
  useEffect(() => {
    if (!editing) setDraft(value === 0 ? '' : String(value));
  }, [value, editing]);

  return (
    <input
      inputMode="numeric"
      value={draft}
      onFocus={(event) => {
        setEditing(true);
        if (value === 0) setDraft('');
        event.currentTarget.select();
      }}
      onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ''))}
      onBlur={() => {
        setEditing(false);
        const next = Number(draft || '0');
        if (next !== value) onSave(next);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(value === 0 ? '' : String(value));
          event.currentTarget.blur();
        }
      }}
      className={cx(
        'tabular w-14 rounded-[var(--radius-sm)] bg-transparent px-1 py-0.5 text-center text-sm',
        'focus:bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]',
        muted && 'text-[var(--color-ink-faint)]',
      )}
    />
  );
}
